import { CatchUpTray, ChannelSpecificCatchUpTray } from '../../../domain/catch-up-tray';
import { CreateCatchUpPageItemApiModel } from '@cxb/editor-stb-api/model/createCatchUpPageItemApiModel';
import { mapperPipe, remainderMapper } from 'osn-shared';
import { CreateChannelSpecificCatchUpPageItemApiModel } from '@cxb/editor-stb-api';
import {
  displayServiceTypeIconMapperDomainToApi,
  layoutMapperDomainToApi,
  nameLocalizedMapperDomainToApi,
  scheduledDateMapperDomainToApi,
  targetingMapperDomainToApi,
} from '../../mapping/create-tray-domain-to-api';
import { CatchUpPageItemApiModel } from '@cxb/editor-stb-api/model/catchUpPageItemApiModel';
import { ChannelSpecificCatchUpPageItemApiModel } from '@cxb/editor-stb-api/model/channelSpecificCatchUpPageItemApiModel';
import {
  commonMapper,
  displayServiceTypeIconMapperApiToDomain,
  mapApiLayoutToDomain,
  nameLocalizedMapperApiToDomain,
  scheduledDateMapperApiToDomain,
  targetingMapperApiToDomain,
} from '../../mapping/create-tray-api-to-domain';
import { CatchUpTrayViewModel, ChannelSpecificCatchUpTrayViewModel } from './catch-up-tray.view-model';
import {
  commonMapperDomainToViewModel,
  displayServiceTypeIconMapper,
  layoutMapper,
  nameLocalizedMapperDomainToViewModel,
  scheduledDateMapperDomainToViewModel,
  targetingMapper,
} from '../create-tray-screen/mapping/create-tray-domain-to-vm';
import {
  commonMapperViewModelToDomain,
  nameLocalizedMapperViewModelToDomain,
  scheduledDateMapperViewModelToDomain,
} from '../create-tray-screen/mapping/create-tray-vm-to-domain';
import { TrayFormViewModelType } from '../view-model-common/view-model-type';

// 'CatchUpTray'
// 'ChannelSpecificCatchUpTray'

/**
 * DOMAIN -> API
 */

export const mapCatchUpTrayDomainToApiModel = (
  item: CatchUpTray | ChannelSpecificCatchUpTray,
  pageVersionId?: string,
  tenantId?: string
): CreateCatchUpPageItemApiModel => {
  const commonProperties = mapperPipe(
    item,
    nameLocalizedMapperDomainToApi,
    targetingMapperDomainToApi,
    scheduledDateMapperDomainToApi,
    layoutMapperDomainToApi,
    displayServiceTypeIconMapperDomainToApi
  );
  return remainderMapper(commonProperties, () => ({
    tenantId,
    pageVersionId,
    items: item.channels.map((channel) => ({
      ...(!!channel.id ? { id: channel.id } : null),
      order: channel.order,
      programId: channel.programId,
      instanceId: channel.instanceId,
      channelId: channel.channel?.id,
      programmingTime: channel.programStartTime,
      programmingEndTime: channel.programEndTime,
    })),
  }));
};

export const mapChannelSpecificCatchUpTrayDomainToApiModel = (
  item: ChannelSpecificCatchUpTray,
  pageVersionId?: string,
  tenantId?: string
): CreateChannelSpecificCatchUpPageItemApiModel => {
  return {
    ...mapCatchUpTrayDomainToApiModel(item, pageVersionId, tenantId),
    channelId: item.channel.id,
  };
};

/**
 * API -> DOMAIN
 */

export const mapCatchUpTrayApiModelToDomainModel = (pageItem: CatchUpPageItemApiModel): CatchUpTray => {
  return remainderMapper(
    mapperPipe(
      pageItem,
      commonMapper,
      nameLocalizedMapperApiToDomain,
      targetingMapperApiToDomain,
      scheduledDateMapperApiToDomain,
      displayServiceTypeIconMapperApiToDomain
    ),
    () => ({
      type: 'CatchUpTray',
      channels: pageItem.items.map((item) => ({
        id: item.id,
        ...(item.channel
          ? {
              channel: {
                id: item.channel?.id,
                channelID: item.channel.channelId,
                channelName: item.channel.channelName,
                channelNumber: item.channel.channelNumber,
              },
            }
          : undefined),

        programStartTime: item.programmingTime,
        programEndTime: item.programmingEndTime,
        programId: item.programId,
        instanceId: item.instanceId,
        order: item.order,
      })),
      layout: mapApiLayoutToDomain(pageItem.layout),
    })
  );
};

export const mapChannelSpecificCatchUpTrayApiModelToDomainModel = (
  pageItem: ChannelSpecificCatchUpPageItemApiModel
): ChannelSpecificCatchUpTray => {
  return {
    ...mapCatchUpTrayApiModelToDomainModel(pageItem as CatchUpPageItemApiModel),
    type: 'ChannelSpecificCatchUpTray',
    channel: {
      channelID: pageItem.channel.channelId,
      channelName: pageItem.channel.channelName,
      channelNumber: pageItem.channel.channelNumber,
      id: pageItem.channel.id,
    },
  };
};

/**
 * DOMAIN -> VIEWMODEL
 */

export const mapCatchUpTrayDomainModelToViewModel = (
  pageItem: CatchUpTray | ChannelSpecificCatchUpTray
): CatchUpTrayViewModel | ChannelSpecificCatchUpTrayViewModel => {
  const commonProperties = mapperPipe(
    pageItem,
    commonMapperDomainToViewModel,
    nameLocalizedMapperDomainToViewModel,
    targetingMapper,
    scheduledDateMapperDomainToViewModel,
    displayServiceTypeIconMapper,
    layoutMapper
  );

  return remainderMapper(commonProperties, () => ({
    type: pageItem.type === 'CatchUpTray' ? TrayFormViewModelType.CatchUpTray : TrayFormViewModelType.ChannelSpecificCatchUpTray,
    channel: pageItem.channel,
    channels: pageItem.channels.map((item) => ({
      id: item.id,
      index: item.order,
      programSelector: {
        channel: { ...(pageItem.type === 'CatchUpTray' ? item.channel : pageItem.channel) },
        programId: item.programId,
        programStartTime: item.programStartTime,
        programEndTime: item.programEndTime,
        instanceId: item.instanceId,
      },
    })),
  }));
};

/**
 * VIEWMODEL -> DOMAIN
 */

export const mapCatchUpTrayViewModelToDomainModel = (
  vm: CatchUpTrayViewModel | ChannelSpecificCatchUpTrayViewModel
): CatchUpTray | ChannelSpecificCatchUpTray => {
  const commonProperties = mapperPipe(
    vm,
    commonMapperViewModelToDomain,
    nameLocalizedMapperViewModelToDomain,
    targetingMapper,
    scheduledDateMapperViewModelToDomain,
    displayServiceTypeIconMapper,
    layoutMapper
  );

  return remainderMapper(commonProperties, () => ({
    type: vm.type === TrayFormViewModelType.CatchUpTray ? 'CatchUpTray' : 'ChannelSpecificCatchUpTray',
    channel: vm.channel,
    channels: vm.channels.map((item, index) => ({
      id: item.id,
      order: index,
      channel: item.programSelector.channel,
      programStartTime: item.programSelector.programStartTime,
      programEndTime: item.programSelector.programEndTime,
      programId: item.programSelector.programId,
      instanceId: item.programSelector.instanceId,
    })),
  }));
};
